export enum ReferralStatus {
    Available = 'Available',
    Provider_Request = 'Provider_Request',
    Matched = 'Matched',
}

export enum PlacementType {
    Assessment_Only = 'Assessment_Only',
    Assessment_with_Concurrent_Treatment = 'Assessment_with_Concurrent_Treatment',
    Treatment_Only = 'Treatment_Only',
    Transfer_Reevaluation = 'Transfer_Reevaluation'
}

export enum ServiceLocationType {
    Home = "Home",
    ClinicCenter = "ClinicCenter",
    Community = "Community",
    Telehealth = "Telehealth",
    TelehealthAssessmentOnly = "TelehealthAssessmentOnly",
    SchoolDayCare = "SchoolDayCare",
}

export enum ServiceType {
    ABA_Parent_Training = 'ABA_Parent_Training',
    ABA_Services_Severe_Behavior = 'ABA_Services_Severe_Behavior',
    Coaching = 'Coaching',
    Group_ABA = 'Group_ABA',
    PLAY_Project = 'PLAY_Project',
    Parent_Led_ABA = 'Parent_Led_ABA',
    Social_Skills_Group = 'Social_Skills_Group',
    Traditional_ABA = 'Traditional_ABA',
    ThreeTierOrParentTraining = 'Three_Tier_OR_Parent_Training',
    ThreeTierOrParentLed = 'Three_Tier_OR_Parent_Led',
    Chat = 'Chat',
    Connect = 'Connect',
    Calibrate = "Calibrate",
    Compass_Behavioral = "Compass_Behavioral",
    Compass_Psychotherapy = "Compass_Psychotherapy",
    Psychotherapy = "Psychotherapy",
    RUBI = "RUBI",
}

export enum ReferralConsideration {
    None = 'None',
    Clinic_based = 'Clinic_based',
    Escalation = 'Escalation',
    Provider_Request = 'Provider_Request',
    Siblings = 'Siblings',
    Translation_Services = 'Translation_Services',
    CPI_Training = 'CPI_Training',
    Out_of_Service_Area = 'Out_of_Service_Area',
}

export enum TreatmentOffered {
    Caregiver_Mediated = "Caregiver_Mediated",
    Practitioner_Mediated = "Practitioner_Mediated",
    Caregiver_And_Practitioner_Mediated = "Caregiver_And_Practitioner_Mediated"
}

const treatmentOfferedLabelMap = new Map<TreatmentOffered, string>([
    [TreatmentOffered.Caregiver_Mediated, "Caregiver Mediated"],
    [TreatmentOffered.Practitioner_Mediated, "Practitioner Mediated"],
    [TreatmentOffered.Caregiver_And_Practitioner_Mediated, "Caregiver and Practitioner Mediated"]
]);

export const treatmentOfferedToLabel = (key: TreatmentOffered) => treatmentOfferedLabelMap.get(key)

export const isTreatmentOfferedCareMedicated = (val: any) => {
    return val === TreatmentOffered.Caregiver_Mediated;
}

export const isTreatmentOfferedPractitionerMedicated = (val: any) => {
    return val === TreatmentOffered.Practitioner_Mediated;
}

export const isTreatmentOfferedCaregiverAndPractitioner = (val: any) => {
    return val === TreatmentOffered.Caregiver_And_Practitioner_Mediated;
}

export const isIncludedPractitioner = (val: any): boolean => {
    return isTreatmentOfferedPractitionerMedicated(val) || isTreatmentOfferedCaregiverAndPractitioner(val);
}

export const includedSocialOrGroupABA = (services: ServiceType[]) =>
    services.includes(ServiceType.Group_ABA) || services.includes(ServiceType.Social_Skills_Group);

export const isCalibrateOrBehavioralOrRUBI = (services: ServiceType[]) =>
    services.includes(ServiceType.RUBI) || services.includes(ServiceType.Calibrate) || services.includes(ServiceType.Compass_Behavioral);

export const decoupleServiceType = (serviceType: ServiceType): ServiceType[] => {
    switch (serviceType) {
        case ServiceType.ThreeTierOrParentLed:
            return [ServiceType.Traditional_ABA, ServiceType.Parent_Led_ABA];
        case ServiceType.ThreeTierOrParentTraining:
            return [ServiceType.Traditional_ABA, ServiceType.ABA_Parent_Training];
        default:
            return [serviceType];
    }
}

export type SiblingReferral = {
    guid: string;
    siblingCaseNumber: string;
    siblingSfCaseId: string;
    siblingReferralGuid: string;
    clientReferralGuid: string;
    hasOffers: boolean;
}

type ClientProviderPreference = {
    guid: string;
    sfProviderId: string;
    clientPreference: string;
    preferenceReason: string;
};

export type Referral = {
    id: string;
    guid: string;
    caseNumber: string;
    serviceLocations: ServiceLocationType[];
    serviceTypes: ServiceType[];
    serviceLocationsParaprofessional: ServiceType[],
    serviceLocationsMidLevel: ServiceType[],
    serviceLocationsHighLevel: ServiceType[],
    serviceLocationsGroupCare: ServiceType[],
    referralConsiderations: ReferralConsideration[];
    status: ReferralStatus;
    placementType: PlacementType;
    county: string;
    location: string;
    clientAge: number;
    requiredLanguage: string;
    clientName: string | null;
    scheduleNotes: string;
    matchedDate?: string | null;
    requestDate?: string | null;
    clientSiblingReferrals: SiblingReferral[];
    myProviderPreferences: ClientProviderPreference[];
} & ReferralEstimatedHours & ReferralAuthorizedHours;
export type ReferralEstimatedHours = {
    directCareHours: string;
    midLevelHours: string;
    highLevelHours: string;
    groupCareHours: string;
}

export type ReferralAuthorizedHours = {
    authDirectCareHours: string;
    authDirectCareBeneficialHours: string;
    authMidLevelHours: string;
    authMidLevelBeneficialHours: string;
    authHighLevelHours: string;
    authHighLevelBeneficialHours: string;
    authGroupCareHours: string;
    authGroupCareBeneficialHours: string;
    careMediatedMidHours: number;
    careMediatedHighHours: number;
    careMediatedMidMinHours: number;
    careMediatedHighMinHours: number;
    practDirectBeneficialHours: number;
    practMidBeneficialHours: number;
    practHighBeneficialHours: number;
    practGroupBeneficialHours: number;
    practDirectOptimalHours: number;
    practMidOptimalHours: number;
    practHighOptimalHours: number;
    practGroupOptimalHours: number;
    authTherapyTreatmentUnitsPer: string;
    authTherapyTreatmentUnitType: string;
    authTherapyTreatmentUnit: number;
}
